/**
 * HighlightsCard Component
 *
 * "What's working": the few findings worth acting on in the period —
 * the cheapest campaign, the cheaper platform, budget spent with nothing
 * to show, and clicks that never turn into tracked conversions.
 */

import React from 'react';
import { Card } from '@/components/ui/Card';
import type { Highlight } from '@/types/analytics';
import { formatCount, formatMoney, platformLabel, resultNoun } from '@/utils/analyticsFormat';

export interface HighlightsCardProps {
  highlights: Highlight[];
  loading?: boolean;
  onOpenCampaign: (highlight: Highlight) => void;
}

function sentence(h: Highlight): { text: React.ReactNode; action?: string } {
  const money = (v?: number) => formatMoney(v ?? null, h.currency ?? null);
  const noun = resultNoun(h.result_type ?? 'conversions');
  switch (h.kind) {
    case 'best_campaign':
      return {
        text: (
          <><strong className="text-ink">{h.campaign_name}</strong> gets {noun} at {money(h.cost_per_result)} each,{' '}
            {h.pct_better}% below your average of {money(h.average)}.</>
        ),
        action: 'Open',
      };
    case 'no_results':
      return {
        text: (
          <><strong className="text-ink">{h.campaign_name}</strong> spent {money(h.spend)} with no {noun} yet.</>
        ),
        action: 'Review',
      };
    case 'platform_cheaper':
      return {
        text: (
          <>Results on <strong className="text-ink">{platformLabel(h.platform ?? '')}</strong> cost {h.pct_better}% less
            than on {platformLabel(h.other_platform ?? '')} ({money(h.cost_per_result)} vs {money(h.other_cost_per_result)}).</>
        ),
      };
    case 'tracking_gap':
      return {
        text: (
          <><strong className="text-ink">{platformLabel(h.platform ?? '')}</strong> sent {formatCount(h.clicks)} clicks but
            recorded no conversions. Check that your pixel or conversion tracking is set up.</>
        ),
      };
    default:
      return { text: null };
  }
}

const TONE_ICON: Record<Highlight['tone'], { path: string; style: string; label: string }> = {
  good: { path: 'M5 13l4 4L19 7', style: 'bg-success-soft text-success', label: 'Working' },
  warning: { path: 'M12 7v6m0 4h.01', style: 'bg-warning-soft text-warning', label: 'Needs attention' },
  info: { path: 'M12 11v6m0-10h.01', style: 'bg-info-soft text-info', label: 'Insight' },
};

export const HighlightsCard: React.FC<HighlightsCardProps> = ({ highlights, loading, onOpenCampaign }) => (
  <Card variant="outlined" padding="none" className="!rounded-2xl px-5 py-[18px] h-full flex flex-col">
    <h3 className="font-display text-[15px] font-bold text-ink">What's working</h3>

    {loading ? (
      <div className="animate-pulse space-y-3 mt-4">
        {[0, 1, 2].map((i) => <div key={i} className="h-10 bg-surface rounded" />)}
      </div>
    ) : highlights.length === 0 ? (
      <p className="text-[13px] text-ink-3 mt-3">
        Nothing to call out yet. Highlights appear once campaigns have a few days of results.
      </p>
    ) : (
      <ul className="mt-3 space-y-3">
        {highlights.map((h, i) => {
          const { text, action } = sentence(h);
          const icon = TONE_ICON[h.tone];
          return (
            <li key={i} className="flex gap-2.5">
              <span className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${icon.style}`}>
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={3} viewBox="0 0 24 24" aria-label={icon.label}>
                  <path strokeLinecap="round" strokeLinejoin="round" d={icon.path} />
                </svg>
              </span>
              <div className="text-[13px] leading-snug text-ink-2">
                {text}
                {action && h.campaign_id && (
                  <button
                    type="button"
                    onClick={() => onOpenCampaign(h)}
                    className="ml-1.5 text-[12px] font-bold text-primary hover:underline"
                  >
                    {action}
                  </button>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    )}
  </Card>
);

export default HighlightsCard;
